import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon, MinusIcon, PlusIcon } from "@radix-ui/react-icons";
import { Box, Button, Card, Flex, Heading, IconButton, Spinner, Text, Tooltip } from "@radix-ui/themes";
import { debounce } from "lodash";
import { useCallback } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CheckboxComponent from "../../components/inputs/Checkbox/CheckboxComponent";
import DateFieldComponent from "../../components/inputs/Datefield/DatefieldComponent";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import TextAreaFieldComponent from "../../components/inputs/TextAreafield/TextAreafieldComponent";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetPaymentTypesQuery } from "../../hooks/useGetPaymentTypesQuery";
import { useGetScholarshipTypesQuery } from "../../hooks/useGetScholarshipTypesQuery";
import { useGetScholarshipProvidersQuery } from "../../hooks/useQueryScholarshipProviders";
import { resetStudentData, setStudentData } from "../../redux/slices/school/student";
import { resetUserData, setUserData } from "../../redux/slices/users/user";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { createScholarship } from "../../services/school/scholarship";
import { getStudentByCode } from "../../services/school/student/getStudentByCode";
import { getUserById } from "../../services/user/getUsers";
import { ScholarshipType } from "../../types/Scholarship";
import { UserPreview } from "../Users/UserPreview";
import { ScholarshipFormValidation } from "./ScholarshipFormValidation";

export const CreateScholarShip = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const studentData = useSelector((state: RootState) => state.student.student);
  const userData = useSelector((state: RootState) => state.user);

  const mutation = useMutation({ mutationFn: createScholarship, mutationKey: "createScholarship" });
  const { data: scholarshipTypes, isLoading: isLoadingScholarshipTypes, isError: errorScholarshipTypes } = useGetScholarshipTypesQuery({ PageNumber: 1, PageSize: 400 });
  const { data: scholarshipProviders, isLoading: isLoadingScholarshipProviders, isError: errorScholarshipProviders } = useGetScholarshipProvidersQuery({ PageNumber: 1, PageSize: 400 });
  const { data: paymentTypes, isLoading: isLoadingPaymentTypes, isError: errorPaymentTypes } = useGetPaymentTypesQuery({ PageNumber: 1, PageSize: 400 });

  const { control, handleSubmit, setValue, getValues, formState: { errors }, watch, reset } = useForm<ScholarshipType>({
    resolver: yupResolver(ScholarshipFormValidation),
    defaultValues: {
      amount: 0,
      percent: 0,
      idPaymentsPercents: [{ idPaymentType: '', percent: 0 }],
    },
    mode: "onBlur",
  });

  const { fields, append, remove, } = useFieldArray({
    control,
    name: 'idPaymentsPercents',
  });
  const { isLoading: isLoadingStudent, refetch: fetchStudentByCode } = useQuery(
    ["getStudentByCode"],
    async () => {
      const studentCodeValue = getValues("studentCode").trim();
      try {
        const studentData = await getStudentByCode(studentCodeValue);
        if (!studentData) return;
        const userData = await getUserById(studentData?.idUser);
        setValue("idStudent", studentData?.id ?? "");
        dispatch(setStudentData(studentData));
        dispatch(setUserData(userData));
      } catch {
        dispatch(resetStudentData());
        dispatch(resetUserData());
      }
    },
    { enabled: false, refetchOnWindowFocus: false, retry: false }
  );

  const handleStudentCodeChange = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      setValue("studentCode", trimmedValue);
      if (trimmedValue.length >= 3) {
        fetchStudentByCode();
      } else {
        dispatch(resetStudentData());
        dispatch(resetUserData());
      }
    }, 300),
    [setValue, fetchStudentByCode]
  );

  const onSubmit = handleSubmit(async (data) => {
    try {
      const scholarshipData: ScholarshipType = {
        ...data,
        idPaymentType: undefined,
        idsPaymentTypes: JSON.stringify(data.idPaymentsPercents),
      };

      await mutation.mutateAsync(scholarshipData);
      toast.success("Bolsa de estudo salva");
      navigate(routes.scholarships);
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handleBack = () => {
    dispatch(resetStudentData());
    dispatch(resetUserData());
    navigate(routes.scholarships);
  };

  return (
    <Card size="5">
      <Flex direction="column" gap="6">
        <form onSubmit={onSubmit} noValidate>

          <TextFieldComponent
            name="studentCode"
            label="Código do estudante"
            control={control}
            errors={errors}
            required={true}
            fieldProps={{
              placeholder: "Introduza o código do estudante",
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleStudentCodeChange(e.target.value),
              onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                e.preventDefault();
                const pastedText = e.clipboardData.getData("text");
                handleStudentCodeChange(pastedText);
              }
            }}
          />

          <Box mt="3">
            <UserPreview />
          </Box>

          <Box pt="3">
            <Flex gap="4">
              <SelectComponent
                name="idScholarshipType"
                label="Tipo de Bolsa"
                control={control}
                required={true}

                errors={errors}
                style={{ width: '100%' }}
                options={scholarshipTypes?.items.map(t => ({
                  value: t.id ?? "",
                  label: t.description ?? ""
                }))}
                itemsLoading={isLoadingScholarshipTypes}
                itemsError={errorScholarshipTypes}
              />
              <SelectComponent
                name="idScholarshipProvider"
                label="Provedor/Financiador"
                control={control}
                required={true}

                errors={errors}
                style={{ width: '100%' }}
                options={scholarshipProviders?.items.map(t => ({
                  value: t.id ?? "",
                  label: t.description ?? ""
                }))}
                itemsLoading={isLoadingScholarshipProviders}
                itemsError={errorScholarshipProviders}
              />
            </Flex>
            <CheckboxComponent
              required={true}
              name="isActive"
              label="Bolsa Activa?"
              control={control}
              errors={errors}
              style={{ width: '100%' }}
            />
            <Flex gap="4" direction={"row"}>
              <DateFieldComponent required={true}
                name="startDate" label="Data de Início" control={control} errors={errors} style={{ width: '100%' }} />
              <DateFieldComponent required={true}
                name="endData" label="Data de Término" control={control} errors={errors} style={{ width: '100%' }} />
            </Flex>
            <Flex gap="4" direction={"column"}>
              {fields.map((field, index) => (
                <Flex key={field.id} direction="column" gap="2" style={{ width: '100%' }}>
                  <Flex align='center' justify='between'>
                    <Heading size='2' weight='bold'>Emolumentos {index + 1}</Heading>
                    <Tooltip content="Remover emolumento">
                      <IconButton
                        type="button"
                        aria-label={`Remover o emolumento ${index + 1}`}
                        color='red'
                        variant='soft'
                        size='2'
                        onClick={(e: any) => {
                          e.preventDefault();
                          remove(index);
                        }}
                      >
                        <MinusIcon width="18" height="18" />
                      </IconButton>
                    </Tooltip>
                  </Flex>
                  <SelectComponent
                    name={`idPaymentsPercents.${index}.idPaymentType`}
                    label="Tipo de emolumento"
                    control={control}
                    required={true}
                    errors={errors}
                    options={paymentTypes?.items.map(t => ({
                      value: t.id ?? "",
                      label: t.description ?? ""
                    }))}
                    itemsLoading={isLoadingPaymentTypes}
                    itemsError={errorPaymentTypes}
                    style={{ width: '100%' }}
                  />
                  <TextFieldComponent
                    name={`idPaymentsPercents.${index}.percent`}
                    label="Percentual (%)"
                    control={control}
                    required={true}
                    errors={errors}
                    fieldProps={{ type: "number", min: 0, max: 100 }}
                    style={{ width: '100%' }} />
                </Flex>
              ))}
              {errors?.idPaymentsPercents?.message && (
                <Text color="red" size="1">{`${errors?.idPaymentsPercents?.message}`}</Text>
              )}
              <Box>
                <Button type="button" variant="soft" onClick={() => append({ idPaymentType: '', percent: 0 })}>
                  <PlusIcon /> Adicionar emolumentos
                </Button>
              </Box>


              <TextAreaFieldComponent
                name="description"
                label="Observações"
                control={control}
                errors={errors}
                style={{ width: '100%' }}
              />
            </Flex>

            <Flex mt="4" justify="end" gap="2">
              <Button type="button" variant="soft" color="gray" onClick={handleBack} disabled={mutation.isLoading}>
                <ChevronLeftIcon /> Voltar
              </Button>
              <Button type="submit" disabled={mutation.isLoading} variant="surface">
                {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                Registar
              </Button>
            </Flex>
          </Box>

        </form>
      </Flex>
    </Card>
  );
};